import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { api } from '@/api';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { PERMISSION_OBJ_WAYBILL } from '@/constants/permissions';
import { waybillStore, campaignStore, cooperativeStore, counterpartyStore, productStore } from '@/stores';
import { productFormattedLabel } from '@/utils/common';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';
import { WaybillStatusType } from '@/types/enums';

import { useMassUpdateCashedStores } from '@/utils/store';
import { DrawerForm } from '@/components/drawer-form';
import { getFormFields } from './setup';

const entity = 'waybills';
const store = waybillStore;

/** Keys allowed in waybill create/update API payload (excludes read-only latest* fields). */
const WAYBILL_PAYLOAD_KEYS = [
  'waybillNumber',
  'productId',
  'campaignId',
  'factoryId',
  'harborId',
  'cooperativeId',
  'exporterId',
  'productNature',
  'declaredNetWeightKg',
  'status',
] as const;

/** Read-only display fields (latest weighing summary); excluded from permission check so they always render. */
const WAYBILL_READONLY_DISPLAY_FIELDS = [
  'latestWeighingCode',
  'latestNatGrade',
  'latestFccGrade',
  'latestBags',
  'latestAcceptedWeight',
];

const pickWaybillPayload = (values: Record<string, unknown>): Record<string, unknown> => {
  return WAYBILL_PAYLOAD_KEYS.reduce(
    (acc, key) => {
      if (Object.hasOwn(values, key)) {
        acc[key] = values[key];
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );
};

/**
 * Returns the latest weighing analysis by date (analysisDate or createdAt DESC).
 * Backend GET /waybills/:id returns analyses sorted latest-first; this helper makes the intent explicit and is resilient to order.
 */
function getLatestWeighingAnalysis(
  analyses: cyrian.api.waybillsWeighingAnalyses.WaybillWeighingAnalysis[] | undefined,
): cyrian.api.waybillsWeighingAnalyses.WaybillWeighingAnalysis | undefined {
  if (!analyses?.length) return undefined;
  const sorted = [...analyses].sort((a, b) => {
    const dateA = a.analysisDate?.getTime() ?? a.createdAt?.getTime() ?? 0;
    const dateB = b.analysisDate?.getTime() ?? b.createdAt?.getTime() ?? 0;
    return dateB - dateA;
  });
  return sorted[0];
}

export const WaybillForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === `base-layout.${entity}.create`;
  const { waybillId } = transition.router.globals.params;
  const [foundItem, setFoundItem] = React.useState<cyrian.api.waybills.Waybill | null>(null);

  useEffect(() => {
    if (!isCreate && waybillId) {
      api.waybills.get(+waybillId).source.then(({ data }) => {
        setFoundItem(data);
      });
    }
  }, [waybillId, isCreate]);

  const { loading } = useMassUpdateCashedStores([campaignStore, cooperativeStore, productStore, counterpartyStore]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const noDataLabel = getTranslatedString(`${entity}.noData`);

  const defaultCampaign =
    isCreate && campaignStore.items.length > 0
      ? campaignStore.items.slice().sort((a, b) => Number(b.year) - Number(a.year))[0]
      : undefined;
  const defaultCampaignId = defaultCampaign?.id;

  const initValues =
    !isCreate && foundItem
      ? (() => {
          const latest = getLatestWeighingAnalysis(foundItem.weighingAnalyses);
          return {
            waybillNumber: foundItem.waybillNumber,
            productId: foundItem.product?.id,
            campaignId: foundItem.campaign?.id,
            productNature: foundItem.productNature,
            declaredNetWeightKg: foundItem.declaredNetWeightKg ?? undefined,
            factoryId: foundItem.factory?.id,
            harborId: foundItem.harbor?.id,
            cooperativeId: foundItem.cooperative?.id,
            exporterId: foundItem.exporter?.id,
            latestWeighingCode: latest?.weighingCode ?? noDataLabel,
            latestNatGrade: latest?.natGrade ?? noDataLabel,
            latestFccGrade: latest?.fccGrade ?? noDataLabel,
            latestBags: latest?.bagsCount ?? noDataLabel,
            latestAcceptedWeight: latest?.acceptedWeight ?? noDataLabel,
            status: foundItem.status,
          };
        })()
      : {
          campaignId: defaultCampaignId,
          status: WaybillStatusType.InProgress,
          latestWeighingCode: noDataLabel,
          latestNatGrade: noDataLabel,
          latestFccGrade: noDataLabel,
          latestBags: noDataLabel,
          latestAcceptedWeight: noDataLabel,
        };

  const title = isCreate
    ? getTranslatedString(`${entity}.create-new`)
    : getTranslatedString(`${entity}.waybill-number`, { 0: waybillId });

  const formFields = getFormFields();
  let allowedFields = getAllowedFormFields(
    formFields,
    isCreate,
    PERMISSION_OBJ_WAYBILL,
    WAYBILL_READONLY_DISPLAY_FIELDS,
  );

  if (!isCreate && foundItem) {
    allowedFields = allowedFields.map((field: any) => {
      const initialSelectedOptionsFor = (key: string) => {
        const map: Record<string, { value: number; label: string }[] | undefined> = {
          productId: foundItem.product
            ? [{ value: foundItem.product.id, label: productFormattedLabel(foundItem.product) }]
            : undefined,
          campaignId: foundItem.campaign
            ? [
                {
                  value: foundItem.campaign.id,
                  label: foundItem.campaign.name?.trim() || foundItem.campaign.year || String(foundItem.campaign.id),
                },
              ]
            : undefined,
          factoryId: foundItem.factory
            ? [{ value: foundItem.factory.id, label: foundItem.factory.name ?? String(foundItem.factory.id) }]
            : undefined,
          harborId: foundItem.harbor
            ? [{ value: foundItem.harbor.id, label: foundItem.harbor.name ?? String(foundItem.harbor.id) }]
            : undefined,
          cooperativeId: foundItem.cooperative
            ? [
                {
                  value: foundItem.cooperative.id,
                  label: foundItem.cooperative.name ?? String(foundItem.cooperative.id),
                },
              ]
            : undefined,
          exporterId: foundItem.exporter
            ? [{ value: foundItem.exporter.id, label: foundItem.exporter.name ?? String(foundItem.exporter.id) }]
            : undefined,
        };
        return map[key];
      };
      const opts = initialSelectedOptionsFor(field.name);
      if (!opts) return field;
      return {
        ...field,
        params: { ...field.params, initialSelectedOptions: opts },
      };
    });
  } else if (isCreate && defaultCampaign != null) {
    const campaignLabel = defaultCampaign.name?.trim() || defaultCampaign.year || String(defaultCampaign.id);
    allowedFields = allowedFields.map((field: any) => {
      if (field.name !== 'campaignId') return field;
      return {
        ...field,
        params: {
          ...field.params,
          initialSelectedOptions: [{ value: defaultCampaign.id, label: campaignLabel }],
        },
      };
    });
  }

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) =>
      api[entity].create(pickWaybillPayload(values)).source.then(async ({ data }: any) => {
        store.addItem(data);
        await store.refresh();
        return { data };
      }),
    update: (values: any) =>
      api[entity]
        .update(foundItem.id, pickWaybillPayload(values))
        .source.then(({ data }: any) => store.updateItem(data)),
  });

  const layout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 12 },
  };

  return (
    <DrawerForm
      {...layout}
      resourceId={waybillId}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading || (!isCreate && !foundItem)}
    />
  );
});
